import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./JournalAuditModal.css";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg || "Dato inválido")
      .join(". ");
  }

  return "No fue posible consultar la auditoría.";
};

const getActionLabel = (action) => {
  const labels = {
    create: "Creación",
    update: "Actualización",
    post: "Contabilización automática",
    void: "Anulación",
    reverse: "Reversión",
  };

  return labels[action] || action;
};

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleString("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export default function JournalAuditModal({
  journalEntry,
  onClose,
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAuditLogs = async () => {
      if (!journalEntry?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const response = await axios.get(
          `${API_URL}/accounting/audit-logs`,
          {
            headers: getHeaders(),
            params: {
              journal_entry_id: journalEntry.id,
              page: 1,
              page_size: 100,
            },
          }
        );

        setLogs(response.data.items || []);
      } catch (error) {
        toast.error(getErrorMessage(error));
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    loadAuditLogs();
  }, [journalEntry?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!journalEntry) {
    return null;
  }

  return (
    <div
      className="journal-audit-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <section
        className="journal-audit-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="journal-audit-title"
      >
        <header className="journal-audit-header">
          <div>
            <span className="journal-audit-kicker">
              Historial de cambios
            </span>

            <h2 id="journal-audit-title">
              Auditoría del comprobante
            </h2>

            <p>{journalEntry.number}</p>
          </div>

          <button
            type="button"
            className="journal-audit-close"
            onClick={onClose}
            aria-label="Cerrar auditoría"
          >
            ×
          </button>
        </header>

        <div className="journal-audit-summary">
          <span>Descripción</span>
          <strong>{journalEntry.description || "Sin descripción"}</strong>
        </div>

        <div className="journal-audit-content">
          {loading ? (
            <div className="journal-audit-empty">
              <span className="journal-audit-spinner" />
              <p>Cargando historial de auditoría...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="journal-audit-empty">
              <span className="journal-audit-empty-icon">◷</span>
              <p>No existen eventos para este comprobante.</p>
            </div>
          ) : (
            <div className="journal-audit-timeline">
              {logs.map((log) => (
                <article
                  className="journal-audit-event"
                  key={log.id}
                >
                  <span
                    className={`journal-audit-dot action-${log.action}`}
                  />

                  <div className="journal-audit-event-card">
                    <div className="journal-audit-event-heading">
                      <strong>{getActionLabel(log.action)}</strong>

                      <time>{formatDate(log.created_at)}</time>
                    </div>

                    <p>{log.description || "Sin detalle registrado."}</p>

                    <small>
                      Registrado por:{" "}
                      <b>{log.user_name || "Sistema"}</b>
                    </small>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}